// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { difference, intersection } from 'lodash';

// Util
import { createCustomEqualShallowSelector, createShallowSelector } from '../milanoteReselect/milanoteReselect';
import { isBoard } from '../../../common/elements/utils/elementTypeUtils';
import { hasNotChanged } from '../react/propsComparisons';

// Selectors
import socketChannelSelector from './socketChannelSelector';
import { getRemoteActiveCurrentlyViewedBoardIds } from './remoteUsersSelector';
import { isGuestSelector } from '../../user/currentUserSelector';
import { getFailedBoardIds, getFetchedBoardIds } from '../../element/board/boardSelector';
import { getElements } from '../../element/selectors/elementSelector';
import { getCurrentBoardId } from '../../element/selectors/currentBoardSelector';
import { markAsyncResourceAsStale } from '../services/http/asyncResource/asyncResourceActions';

// Constants
import { ResourceTypes } from '../services/http/asyncResource/asyncResourceConstants';

const EMPTY_ARRAY = [];

const shallowPotentiallyStaleElementIdsSelector = createShallowSelector(
    socketChannelSelector,
    getRemoteActiveCurrentlyViewedBoardIds,
    getCurrentBoardId,
    isGuestSelector,
    ({ channels }, viewedBoardIds, currentBoardId, isGuest) =>
        isGuest
            ? EMPTY_ARRAY
            : // Any board that is currently being viewed and we are not connected to the channel of could be stale
              // Don't include the current board though if it somehow ends up there, as we don't want to mark it
              // as stale (this will cause the board loader to show). This was a problem on the iPad.
              difference(viewedBoardIds, channels, [currentBoardId]),
);

const retrievedStaleElementIdsSelector = createShallowSelector(
    shallowPotentiallyStaleElementIdsSelector,
    getFetchedBoardIds,
    getFailedBoardIds,
    isGuestSelector,
    (potentiallyStaleElementIds, retrievedBoardIds, failedBoardIds, isGuest) => {
        if (isGuest) return EMPTY_ARRAY;

        const validRetrievedBoardIds = difference(retrievedBoardIds, failedBoardIds);

        // The stale elements are any elements that have been retrieved and are being edited by remote users in
        // channels that the current user is not connected to.
        return intersection(potentiallyStaleElementIds, validRetrievedBoardIds);
    },
);

const staleElementsHaveNotChanged = hasNotChanged(1);

const retrievedStaleBoardsSelectorEqualityChecker = (prevArgs, nextArgs) =>
    staleElementsHaveNotChanged(prevArgs, nextArgs);

const createCustomStaleBoardsSelector = createCustomEqualShallowSelector(retrievedStaleBoardsSelectorEqualityChecker);

// Potentially expensive operation, so only want to execute this if we really need to
const retrievedStaleBoardIdsSelector = createCustomStaleBoardsSelector(
    getElements,
    retrievedStaleElementIdsSelector,
    (elements, staleElementIds) =>
        // If an element is not a board, we can never view it, so it can never actually be stale and we therefore
        // shouldn't mark it as stale
        staleElementIds.filter((elementId) => isBoard(elements.get(elementId))),
);

const mapStateToProps = createSelector(retrievedStaleBoardIdsSelector, isGuestSelector, (staleBoardIds, isGuest) => ({
    staleBoardIds,
    isGuest,
}));

const mapDispatchToProps = (dispatch) => ({
    dispatchMarkElementsAsStale: (ids) => dispatch(markAsyncResourceAsStale(ResourceTypes.boards, ids)),
});

/**
 * Figures out which elements are currently being viewed by remote users and not the current user and marks those
 * elements as stale by firing a ELEMENTS_STALE action.
 */
@connect(mapStateToProps, mapDispatchToProps)
class StaleBoardManager extends React.Component {
    componentWillMount() {
        const { staleBoardIds, dispatchMarkElementsAsStale, isGuest } = this.props;
        if (isGuest) return;

        staleBoardIds.length && dispatchMarkElementsAsStale(staleBoardIds);
    }

    componentWillReceiveProps(nextProps) {
        const { staleBoardIds, dispatchMarkElementsAsStale, isGuest } = nextProps;
        if (isGuest) return;

        if (this.props.staleBoardIds !== staleBoardIds && staleBoardIds.length) {
            dispatchMarkElementsAsStale(staleBoardIds);
        }
    }

    render() {
        return null;
    }
}

StaleBoardManager.propTypes = {
    staleBoardIds: PropTypes.array,
    dispatchMarkElementsAsStale: PropTypes.func,
    isGuest: PropTypes.bool,
};

export default StaleBoardManager;
